import React, { useCallback, useMemo } from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form";
import {
  useKivunovaTranslation,
  useRwandaLocation
} from "@kivunova/kivufrontendcommon";
import { getNestedFieldErrors } from "./companyAddressFormUtils";

interface RwandaCompanyAddressFormProps {
  control: Control<Record<string, unknown>>;
  errors: FieldErrors;
  watch: UseFormWatch<Record<string, unknown>>;
  setValue: UseFormSetValue<Record<string, unknown>>;
  fieldPrefix: string;
  idPrefix?: string;
  selectSx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  size?: "small" | "medium";
}

const RwandaCompanyAddressForm: React.FC<RwandaCompanyAddressFormProps> = ({
  control,
  errors,
  watch,
  setValue,
  fieldPrefix,
  idPrefix = "",
  selectSx,
  inputSx,
  size = "medium"
}) => {
  const { t } = useKivunovaTranslation();
  const {
    getProvinces,
    getDistrictByProvince,
    getSectors,
    getCells,
    getVillages,
    loading
  } = useRwandaLocation();

  const locationKey = useCallback(
    (type: string, name: string) => `location-${type}-${name}`,
    []
  );

  const selectedProvince = (watch(`${fieldPrefix}.province`) as string) || "";
  const selectedDistrict = (watch(`${fieldPrefix}.district`) as string) || "";
  const selectedSector = (watch(`${fieldPrefix}.sector`) as string) || "";
  const selectedCell = (watch(`${fieldPrefix}.cell`) as string) || "";
  const selectedVillage = (watch(`${fieldPrefix}.village`) as string) || "";

  const provinces = useMemo(() => getProvinces(), [getProvinces]);
  const districts = useMemo(
    () => (selectedProvince ? getDistrictByProvince(selectedProvince) : []),
    [selectedProvince, getDistrictByProvince]
  );
  const sectors = useMemo(
    () =>
      selectedProvince && selectedDistrict
        ? getSectors(selectedProvince, selectedDistrict)
        : [],
    [selectedProvince, selectedDistrict, getSectors]
  );
  const cells = useMemo(
    () =>
      selectedProvince && selectedDistrict && selectedSector
        ? getCells(selectedProvince, selectedDistrict, selectedSector)
        : [],
    [selectedProvince, selectedDistrict, selectedSector, getCells]
  );
  const villages = useMemo(
    () =>
      selectedProvince && selectedDistrict && selectedSector && selectedCell
        ? getVillages(
            selectedProvince,
            selectedDistrict,
            selectedSector,
            selectedCell
          )
        : [],
    [
      selectedProvince,
      selectedDistrict,
      selectedSector,
      selectedCell,
      getVillages
    ]
  );

  const addressErrors = getNestedFieldErrors(errors, fieldPrefix);

  const handleProvinceChange = (val: string) => {
    setValue(`${fieldPrefix}.province` as never, val as never);
    setValue(`${fieldPrefix}.district` as never, "" as never);
    setValue(`${fieldPrefix}.sector` as never, "" as never);
    setValue(`${fieldPrefix}.cell` as never, "" as never);
    setValue(`${fieldPrefix}.village` as never, "" as never);
  };

  const handleDistrictChange = (val: string) => {
    setValue(`${fieldPrefix}.district` as never, val as never);
    setValue(`${fieldPrefix}.sector` as never, "" as never);
    setValue(`${fieldPrefix}.cell` as never, "" as never);
    setValue(`${fieldPrefix}.village` as never, "" as never);
  };

  const handleSectorChange = (val: string) => {
    setValue(`${fieldPrefix}.sector` as never, val as never);
    setValue(`${fieldPrefix}.cell` as never, "" as never);
    setValue(`${fieldPrefix}.village` as never, "" as never);
  };

  const handleCellChange = (val: string) => {
    setValue(`${fieldPrefix}.cell` as never, val as never);
    setValue(`${fieldPrefix}.village` as never, "" as never);
  };

  return (
    <>
      <Controller
        name={`${fieldPrefix}.province`}
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size={size}>
            <InputLabel shrink id={`${idPrefix}province-select-label`}>
              {t("province", "Province")}
            </InputLabel>
            <Select
              {...field}
              required
              label={t("province", "Province")}
              labelId={`${idPrefix}province-select-label`}
              onChange={(e) => {
                field.onChange(e);
                handleProvinceChange(e.target.value);
              }}
              disabled={loading}
              error={!!addressErrors?.province}
              id={`${idPrefix}province`}
              inputProps={
                idPrefix ? { "data-testid": `${idPrefix}province` } : undefined
              }
              sx={selectSx}
            >
              {provinces.map((prov) => (
                <MenuItem
                  key={prov}
                  value={prov}
                  id={`${idPrefix}province-${prov}`}
                >
                  {t(locationKey("province", prov), prov)}
                </MenuItem>
              ))}
            </Select>
            {addressErrors?.province?.message && (
              <FormHelperText error>
                {String(addressErrors.province.message)}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />

      {selectedProvince && (
        <Controller
          name={`${fieldPrefix}.district`}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size={size}>
              <InputLabel shrink id={`${idPrefix}district-select-label`}>
                {t("district", "District")}
              </InputLabel>
              <Select
                {...field}
                required
                labelId={`${idPrefix}district-select-label`}
                label={t("district", "District")}
                onChange={(e) => {
                  field.onChange(e);
                  handleDistrictChange(e.target.value);
                }}
                error={!!addressErrors?.district}
                id={`${idPrefix}district`}
                inputProps={
                  idPrefix
                    ? { "data-testid": `${idPrefix}district` }
                    : undefined
                }
                sx={selectSx}
              >
                {districts.map((dist: string) => (
                  <MenuItem
                    key={dist}
                    value={dist}
                    id={`${idPrefix}district-${dist}`}
                  >
                    {t(locationKey("district", dist), dist)}
                  </MenuItem>
                ))}
              </Select>
              {addressErrors?.district?.message && (
                <FormHelperText error>
                  {String(addressErrors.district.message)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}

      {selectedDistrict && (
        <Controller
          name={`${fieldPrefix}.sector`}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size={size}>
              <InputLabel shrink id={`${idPrefix}sector-select-label`}>
                {t("sector", "Sector")}
              </InputLabel>
              <Select
                {...field}
                required
                labelId={`${idPrefix}sector-select-label`}
                label={t("sector", "Sector")}
                onChange={(e) => {
                  field.onChange(e);
                  handleSectorChange(e.target.value);
                }}
                error={!!addressErrors?.sector}
                id={`${idPrefix}sector`}
                inputProps={
                  idPrefix ? { "data-testid": `${idPrefix}sector` } : undefined
                }
                sx={selectSx}
              >
                {sectors.map((s: string) => (
                  <MenuItem key={s} value={s} id={`${idPrefix}sector-${s}`}>
                    {t(locationKey("sector", s), s)}
                  </MenuItem>
                ))}
              </Select>
              {addressErrors?.sector?.message && (
                <FormHelperText error>
                  {String(addressErrors.sector.message)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}

      {selectedSector && (
        <Controller
          name={`${fieldPrefix}.cell`}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size={size}>
              <InputLabel shrink id={`${idPrefix}cell-select-label`}>
                {t("cell", "Cell")}
              </InputLabel>
              <Select
                {...field}
                required
                labelId={`${idPrefix}cell-select-label`}
                label={t("cell", "Cell")}
                onChange={(e) => {
                  field.onChange(e);
                  handleCellChange(e.target.value);
                }}
                error={!!addressErrors?.cell}
                id={`${idPrefix}cell`}
                inputProps={
                  idPrefix ? { "data-testid": `${idPrefix}cell` } : undefined
                }
                sx={selectSx}
              >
                {cells.map((c: string) => (
                  <MenuItem key={c} value={c} id={`${idPrefix}cell-${c}`}>
                    {t(locationKey("cell", c), c)}
                  </MenuItem>
                ))}
              </Select>
              {addressErrors?.cell?.message && (
                <FormHelperText error>
                  {String(addressErrors.cell.message)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}

      {selectedCell && (
        <Controller
          name={`${fieldPrefix}.village`}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size={size}>
              <InputLabel shrink id={`${idPrefix}village-select-label`}>
                {t("village", "Village")}
              </InputLabel>
              <Select
                {...field}
                required
                labelId={`${idPrefix}village-select-label`}
                label={t("village", "Village")}
                error={!!addressErrors?.village}
                id={`${idPrefix}village`}
                inputProps={
                  idPrefix ? { "data-testid": `${idPrefix}village` } : undefined
                }
                sx={selectSx}
              >
                {villages.map((v: string) => (
                  <MenuItem key={v} value={v} id={`${idPrefix}village-${v}`}>
                    {t(locationKey("village", v), v)}
                  </MenuItem>
                ))}
              </Select>
              {addressErrors?.village?.message && (
                <FormHelperText error>
                  {String(addressErrors.village.message)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}

      {selectedVillage && (
        <Controller
          name={`${fieldPrefix}.streetAddress`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t("street-address", "Street Address")}
              fullWidth
              size={size}
              error={!!addressErrors?.streetAddress}
              helperText={
                addressErrors?.streetAddress?.message
                  ? String(addressErrors.streetAddress.message)
                  : undefined
              }
              inputProps={
                idPrefix
                  ? { "data-testid": `${idPrefix}street-address` }
                  : undefined
              }
              sx={inputSx}
            />
          )}
        />
      )}
    </>
  );
};

export default RwandaCompanyAddressForm;
